// @ts-strict-ignore
import * as React from 'react';
import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';
import tw from 'twin.macro';

import { Tooltip } from '@/atoms/Tooltip/Tooltip';
import {
  Menu,
  NeedlMenuItem,
  NeedlMenuPopover,
} from '@/components/Atoms/Menu/RadixMenu';
import { MenuButton } from '@/components/Atoms/Menu/RadixMenu/Button';
import { Button } from '@/components/Button/Button';
import type { ListBoxType } from '@/components/ListBox/ListBox';
import { OPTIONS, PriorityList } from '@/components/PriorityList';
import { BulkPriorityUpdateModal } from '@/features/Connect/components/BulkPriorityUpdateModal';
import { StyledArrow } from '@/features/Connect/components/FeedsSelector/FeedsSelector';
import { useFetchWebsite } from '@/features/Connect/hooks/useFetchWebsite';
import { useUpdateRSSPriority } from '@/features/Connect/hooks/useUpdateRSSPriority';
import type { FetchRssSource, Priority } from '@/types';

export const PrioritySelector = ({
  disablePortal = false,
  sourceId,
  priority,
  displayFull = false,
  arrow = true,
  size = 'md',
}: PriorityImplType) => {
  const editRss = useUpdateRSSPriority();

  const updateRssPriority = (priority: Priority) => {
    editRss.mutate({
      source_id: sourceId,
      priority: priority,
    });
  };

  return (
    <Tooltip label='Priority'>
      <PriorityList
        arrow={arrow}
        id={`priority-list-${sourceId}`}
        size={size}
        label='priority-list'
        value={priority}
        onChange={updateRssPriority}
        disabled={editRss.isLoading}
        disablePortal={disablePortal ? 'scroll' : false}
        {...(!displayFull && { getOptionLabel: labelRenderer })}
      />
    </Tooltip>
  );
};

export const BulkPrioritySelector = ({
  rssSources,
}: BulkPrioritySelectorType) => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [showBulkPriorityModal, setShowBulkPriorityModal] =
    React.useState(false);
  const [selectedPriority, setSelectedPriority] = React.useState<
    Priority | undefined
  >();

  const handleBulkPriorityUpdate = (priority: Priority) => {
    setSelectedPriority(priority);
    setShowBulkPriorityModal(true);
  };

  return (
    <>
      <Menu onOpenChange={(open) => setIsMenuOpen(open)}>
        <Tooltip label={`Please select 50 or less sources to proceed`}>
          <MenuButton
            disabled={rssSources.length === 0 || rssSources.length > 50}
          >
            <Button
              variant='action'
              id='BulkPrioritySelector'
              size='medium'
              disabled={rssSources.length === 0 || rssSources.length > 50}
              endIcon={<StyledArrow open={isMenuOpen} />}
            >
              Priority
            </Button>
          </MenuButton>
        </Tooltip>
        <NeedlMenuPopover align='start'>
          {OPTIONS.map(({ value, icon, label }: OptionType) => (
            <NeedlMenuItem
              key={value}
              onSelect={() => handleBulkPriorityUpdate(value)}
            >
              {icon}
              {label}
            </NeedlMenuItem>
          ))}
        </NeedlMenuPopover>
      </Menu>
      <BulkPriorityUpdateModal
        showModal={showBulkPriorityModal}
        rssSources={rssSources}
        onDismiss={() => setShowBulkPriorityModal(false)}
        selectedPriority={selectedPriority}
      />
    </>
  );
};

export const PrioritySelectorWithFetch = ({
  disablePortal,
  sourceId,
  displayFull = false,
  showSkeleton = false,
  arrow = true,
  size = 'md',
}: PriorityType) => {
  const { isLoading, sources } = useFetchWebsite(sourceId);

  if (isLoading) {
    if (showSkeleton) {
      return <Skeleton width='100px' height='32px' />;
    } else {
      return null;
    }
  }

  if (!sources?.[0]?.priority) return null;

  return (
    <PrioritySelector
      arrow={arrow}
      size={size}
      sourceId={sourceId}
      priority={sources[0].priority}
      displayFull={displayFull}
      disablePortal={disablePortal}
    />
  );
};

type PriorityImplType = Omit<PriorityType, 'showSkeleton'> & {
  priority: Priority;
};

type PriorityType = Pick<
  ListBoxType<OptionType>,
  'arrow' | 'disablePortal' | 'size'
> & {
  sourceId: string;
  displayFull?: boolean;
  showSkeleton?: boolean;
};

type BulkPrioritySelectorType = Omit<PriorityType, 'sourceId'> & {
  rssSources: FetchRssSource[];
};

type OptionType = {
  value: Priority;
  icon: JSX.Element;
  label: string;
};

const Container = styled.span`
  ${tw`cursor-pointer`}
`;

const labelRenderer = ({ icon }: OptionType): JSX.Element => {
  return (
    <Container>
      <span>{icon}</span>
    </Container>
  );
};
